/**
 * 申請の下書き(新しい申請ウィザードの状態)。6 種別すべて同じ入口(kind で出し入れを変える)。
 *
 * 旧 RequestForm(Modal)の状態・問い合わせ・送信をそのまま持ち上げたもの。入力欄の意味・
 * 検証・API 呼び出しは変えていない。画面(対象者 / 内容 / 確認の各段)は RequestForm.tsx。
 *
 * 入社申請は、まだ登録していない人を対象者段で入力して従業員ごと作る(newEmployee.ts)。その人を
 * `opts.employee` で外から渡す。対象者の候補は引かず、転記欄はその入力中の値から見せる
 * (送信は呼び元が `requestBody()` で POST /api/hr/onboarding へ。`create` / `submit` は使わない)。
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

import type {
  Employee,
  OnboardingRequestInput,
  TemplateField,
  TemplateItem,
  TemplateStep,
  WfTemplate,
} from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { useDebounced } from "../../lib/useDebounced";
import type { KvItem } from "../../ui";
import { STATUS_FOR_REQUEST, empSourceLabel, kindLabel, statusLabel } from "./constants";
import { useGroups } from "./groupHooks";
import { errorText, useCustomFields, useOrgUnits, usePositions } from "./hooks";
import { useLoans } from "./loanHooks";
import { useAssignments, useInvalidateWf, useTemplate, useTemplates, useWfDefaults } from "./wfHooks";

/** 日付欄の見出し。種別ごとに意味が違う(同じ start_date に入る)。 */
export const DATE_LABEL: Record<string, string> = {
  onboard: "入社日",
  offboard: "退社日",
  transfer: "着任日",
  update: "適用日",
  leave: "休職開始日",
  reinstate: "復職日",
};

/** 種別の説明(旧 5 画面の冒頭文)。種別を選ぶ段と見出しの下に出す。 */
export const KIND_DESC: Record<string, string> = {
  onboard: "入社する人をその場で入力し、従業員の登録と一緒に出す。承認が揃うと在籍状態が「在籍」になり、SaaS の発行・準備タスク・社外依頼が起票される。",
  offboard: "在籍中・休職中の人に出せる。SaaS を選ばなければ今あるべきものをすべて停止対象にする。",
  transfer: "在籍中の人に出せる。異動先の組織は必須。在籍状態は動かさない。",
  update: "在籍中の貸与・SaaS の追加/返却・所属変更。同時に複数出してよい(進行中でも新しく出せる)。",
  leave: "在籍中の人に出せる。退社と同じく停止・回収の向き。",
  reinstate: "休職中の人に出せる。入社と同じく発行の向き。",
};

/** 入力欄のキー。サーバー側の field_key(section,name) と同じ規則。 */
export function fieldKey(f: { section: string; name: string }): string {
  return f.section ? `${f.section}/${f.name}` : f.name;
}

/** 転記元(`source` = "extra:<key>" / "emp:<列>")を持つ入力欄に、対象者から入る値。
 *  `fixed` は従業員マスタの列で、サーバーが申請時に埋める(ここで見せるだけで直せない)。 */
export type SourceValue =
  | { state: "none" }
  | { state: "no_employee" | "empty" | "mismatch" | "ok" | "fixed"; key: string; value: string };

/** 転記元になる従業員。登録済みの人(Employee)か、入社申請で入力中の人(id 無し)。 */
export type DraftEmployee = Pick<
  Employee,
  | "name"
  | "emp_code"
  | "email"
  | "status"
  | "family_name"
  | "given_name"
  | "family_name_kana"
  | "given_name_kana"
  | "department"
  | "employment_type"
  | "extra"
> & { id?: number };

/** 従業員マスタの列("emp:<列>")の値。サーバー(workflow.py emp_source_value)と同じ組み立て。 */
export function empSourceValue(source: string, emp: DraftEmployee): string {
  const key = source.slice("emp:".length);
  if (key === "kana") return [emp.family_name_kana, emp.given_name_kana].map((x) => (x ?? "").trim()).filter(Boolean).join(" ");
  const v = (emp as unknown as Record<string, unknown>)[key];
  return typeof v === "string" ? v.trim() : "";
}

export function sourceValue(f: TemplateField, emp: DraftEmployee | undefined): SourceValue {
  const source = f.source ?? "";
  if (source.startsWith("emp:")) {
    const key = source.slice("emp:".length);
    if (!emp) return { state: "no_employee", key, value: "" };
    return { state: "fixed", key, value: empSourceValue(source, emp) };
  }
  if (!source.startsWith("extra:")) return { state: "none" };
  const key = source.slice("extra:".length);
  if (!emp) return { state: "no_employee", key, value: "" };
  const value = (emp.extra?.[key] ?? "").trim();
  if (!value) return { state: "empty", key, value };
  // 選択の欄は選択肢に無い値を入れない(申請時に 400 になる)。綴りを従業員側と揃えてもらう
  if (f.ftype === "select" && !(f.options ?? []).includes(value)) {
    return { state: "mismatch", key, value };
  }
  return { state: "ok", key, value };
}

/** セクションごとに分けた Checkbox.Group の onChange は、その Group の選択肢に絞った配列を返す。
 *  そのまま setState すると他のセクションのチェックが消えるので、この Group 以外の分を残して差し替える。 */
export function replaceWithin(prev: number[], groupIds: number[], picked: number[]): number[] {
  const g = new Set(groupIds);
  return [...prev.filter((id) => !g.has(id)), ...picked.filter((id) => g.has(id))];
}

export type Group = {
  key: string;
  name: string;
  fields: TemplateField[];
  items: TemplateItem[];
  steps: TemplateStep[];
};

/** セクション順に「入力欄 → SaaS → 工程」でまとめる(親なしは最後)。 */
export function groups(tpl: WfTemplate | undefined): Group[] {
  if (!tpl) return [];
  const out: Group[] = tpl.sections.map((s) => ({
    key: String(s.id),
    name: s.name,
    fields: [],
    items: [],
    steps: [],
  }));
  const loose: Group = { key: "none", name: "親なし", fields: [], items: [], steps: [] };
  const find = (sid: number | null) =>
    (sid != null ? out.find((g) => g.key === String(sid)) : undefined) ?? loose;
  for (const f of tpl.fields) find(f.section_id).fields.push(f);
  for (const i of tpl.items) find(i.section_id).items.push(i);
  for (const s of tpl.steps) find(s.section_id).steps.push(s);
  const all = [...out, loose];
  return all.filter((g) => g.fields.length || g.items.length || g.steps.length);
}

/** 選んだ SaaS 項目に GWS のアカウント発行(connector が gws)が含まれるか。
 * 含むなら入社申請の対象者のメールが必須(アカウントの宛先になる。自動・手動は問わない)。
 * サーバーの create_onboarding も同じ判定で 400 を返す */
export function includesGws(items: TemplateItem[], itemIds: number[]): boolean {
  return items.some((i) => i.connector === "gws" && itemIds.includes(i.item_id));
}

export function itemTag(item: TemplateItem): string {
  if (item.fulfillment === "auto") return `自動(${item.connector || "コネクタ"})`;
  if (item.fulfillment === "external") return "社外依頼";
  return item.default_owner ? `手作業(${item.default_owner})` : "手作業";
}

export type RequestDraft = ReturnType<typeof useRequestDraft>;

export type RequestDraftOptions = {
  /** 入社申請で入力中の人(まだ id が無い)。渡すと対象者の候補を引かず、これを対象者にする */
  employee?: DraftEmployee;
};

/**
 * @param kind   WF の種別。空のあいだは問い合わせを止める(種別を選ぶ段)
 * @param onCreated 申請が通ったら申請番号で呼ぶ(詳細へ飛ぶ)
 * @param opts   外から渡す対象者(`RequestDraftOptions`)
 */
export function useRequestDraft(
  kind: string,
  onCreated: (requestId: number) => void,
  opts: RequestDraftOptions = {},
) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const enabled = !!kind;
  const external = opts.employee;
  const templates = useTemplates(kind);
  const defaults = useWfDefaults();
  const units = useOrgUnits(true);
  const positions = usePositions(true);
  const customFields = useCustomFields(false);

  const [empId, setEmpId] = useState<number | undefined>();
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [startDate, setStartDate] = useState("");
  const [itemIds, setItemIds] = useState<number[]>([]);
  const [removeItemIds, setRemoveItemIds] = useState<number[]>([]);
  const [stepIds, setStepIds] = useState<number[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [approverKind, setApproverKind] = useState<string>("");
  const [approverId, setApproverId] = useState<number | undefined>(undefined);
  const [orgUnitId, setOrgUnitId] = useState<number | undefined>();
  const [positionId, setPositionId] = useState<number | undefined>();
  const [employmentType, setEmploymentType] = useState<string | undefined>();
  const [returnLoanIds, setReturnLoanIds] = useState<number[]>([]);

  const tpl = useTemplate(templateId);
  const assignments = useAssignments(kind === "update" ? empId : undefined);
  // 変更申請で「個体を名指しで返す」ための貸与中の台帳行
  const openLoans = useLoans({
    open: true,
    employeeId: empId,
    enabled: kind === "update" && !!empId,
  });
  const activeItemIds = useMemo(
    () =>
      new Set(
        (assignments.data?.items ?? []).filter((a) => a.desired === "active").map((a) => a.item_id),
      ),
    [assignments.data],
  );

  /** 対象者の候補。種別ごとの在籍状態も検索語も**サーバーで**絞る。
   *
   * 一覧は 200 件で切れるので、**絞り込みはサーバーに投げる**。従業員が 200 人を
   * 超えている組織で、社員番号を打っても新しい人が候補に出てこなかった(id 昇順の
   * 先頭 200 件しか取っていなかった)。`q` は社員番号・氏名・メール・所属の部分一致。
   * 在籍状態を手元で捨てると limit が絞り込み前に効いてしまうので `status` も渡す。
   */
  const [search, setSearch] = useState("");
  const term = useDebounced(search);
  const employees = useQuery({
    queryKey: ["hr", "employees", "wf-candidates", kind, term],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: "200" });
      for (const s of STATUS_FOR_REQUEST[kind] ?? []) params.append("status", s);
      if (term) params.set("q", term);
      return api.employees(params, signal);
    },
    enabled: enabled && !external,
    staleTime: 30_000,
  });
  const candidates: Employee[] = useMemo(() => employees.data?.items ?? [], [employees.data]);
  const selected: DraftEmployee | undefined = useMemo(
    () => external ?? candidates.find((e) => e.id === empId),
    [external, candidates, empId],
  );

  // 種別が変わったら下書きを空にする(出せる在籍状態が種別ごとに違うので対象者も含めて)
  const lastKind = useRef(kind);
  useEffect(() => {
    if (lastKind.current === kind) return;
    lastKind.current = kind;
    setEmpId(undefined);
    setSearch("");
    setStartDate("");
    setItemIds([]);
    setRemoveItemIds([]);
    setStepIds([]);
    setFields({});
    setOrgUnitId(undefined);
    setPositionId(undefined);
    setEmploymentType(undefined);
    setReturnLoanIds([]);
    setApproverKind("");
    setApproverId(undefined);
    setPickerSearch("");
    setTemplateId(defaults.data?.default_templates?.[kind]);
    previousSources.current = { context: "", values: {} };
  }, [kind, defaults.data]);

  // 既定テンプレートは後から届く。**まだ選んでいないときだけ**入れる(選び直した
  // あとに上書きしない)
  useEffect(() => {
    if (!enabled) return;
    const def = defaults.data?.default_templates?.[kind];
    if (def == null) return;
    setTemplateId((prev) => prev ?? def);
  }, [enabled, kind, defaults.data]);

  // テンプレートが決まったら必須の SaaS・工程を選んだ状態にする
  useEffect(() => {
    if (!tpl.data) return;
    setItemIds(tpl.data.items.filter((i) => i.required).map((i) => i.item_id));
    setStepIds(tpl.data.steps.filter((s) => s.required).map((s) => s.id));
    setFields((prev) => {
      const next = { ...prev };
      for (const f of tpl.data!.fields) next[fieldKey(f)] = next[fieldKey(f)] ?? "";
      return next;
    });
  }, [tpl.data]);

  // emp: は固定表示。extra: は欄ごとに元の値が変わったときだけ転記し、手修正を保つ。
  // 対象者・種別・テンプレートが変われば、同じ値でも新しい申請の初期値に戻す。
  const previousSources = useRef<{ context: string; values: Record<string, string> }>({ context: "", values: {} });
  useEffect(() => {
    if (!tpl.data || !selected) return;
    const context = `${kind}:${selected.id ?? "draft"}:${tpl.data.id}`;
    const previous = previousSources.current;
    const values: Record<string, string> = {};
    const updates: Record<string, string> = {};
    for (const f of tpl.data.fields) {
      const v = sourceValue(f, selected);
      const key = fieldKey(f);
      if (v.state === "fixed") {
        updates[key] = v.value;
      } else if (f.source?.startsWith("extra:") && v.state !== "none" && v.state !== "no_employee") {
        // 表示結果が同じ(空・選択肢不一致)でも、転記元自体の変化は検知する。
        const source = JSON.stringify([f.source, selected.extra?.[f.source.slice("extra:".length)] ?? ""]);
        values[key] = source;
        if (previous.context !== context || previous.values[key] !== source) {
          updates[key] = v.state === "ok" ? v.value : "";
        }
      }
    }
    previousSources.current = { context, values };
    setFields((prev) => Object.entries(updates).some(([key, value]) => prev[key] !== value)
      ? { ...prev, ...updates } : prev);
  }, [kind, tpl.data, selected]);

  // 「従業員」型の入力欄と承認者(個人)の候補。対象者と同じく絞り込みはサーバーに投げる
  // (退職者は候補にしない)。フォーム内の Select はどれも同じ検索語を共有する
  const [pickerSearch, setPickerSearch] = useState("");
  const pickerTerm = useDebounced(pickerSearch);
  const needsPicker = enabled && ((tpl.data?.fields ?? []).some((f) => f.ftype === "employee") || approverKind === "employee");
  const picker = useQuery({
    queryKey: ["hr", "employees", "wf-picker", pickerTerm],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: "200" });
      for (const s of ["active", "on_leave", "pre_onboard"]) params.append("status", s);
      if (pickerTerm) params.set("q", pickerTerm);
      return api.employees(params, signal);
    },
    enabled: needsPicker,
    staleTime: 30_000,
  });
  const pickerCandidates: Employee[] = useMemo(() => picker.data?.items ?? [], [picker.data]);

  // 承認者の指定(任意)。選んだら該当者をサーバーで下見する(ログインの有無も)
  const groupsQ = useGroups(true);
  const approverPreview = useQuery({
    queryKey: ["hr", "wf-approvers", approverKind, approverId ?? 0],
    queryFn: () => api.wfApprovers(approverKind, approverId!),
    enabled: enabled && !!approverKind && approverId != null,
    staleTime: 30_000,
  });

  /** 入力欄の下に出す転記の説明。転記元の無い欄は undefined。 */
  const sourceNote = (f: TemplateField): string | undefined => {
    const v = sourceValue(f, selected);
    if (v.state === "fixed") {
      const label = empSourceLabel(f.source);
      return v.value ? `従業員の「${label}」を転記する(ここでは直せない)` : `従業員の「${label}」が未登録(空で申請される)`;
    }
    if (v.state === "none") return undefined;
    const label = customFields.data?.items.find((c) => c.key === v.key)?.label ?? v.key;
    switch (v.state) {
      case "no_employee":
        return `従業員の「${label}」から転記`;
      case "empty":
        return `従業員の「${label}」が未入力なので転記なし`;
      case "mismatch":
        return `従業員の「${label}」は「${v.value}」だが選択肢に無いので転記していない`;
      case "ok":
        return `従業員の「${label}」から転記した(直せる)`;
    }
  };

  /** 「emp:」転記欄に見せる値。社員番号を空で登録する人は採番前なので「(自動採番)」 */
  const fixedText = (f: TemplateField): string => {
    const v = fields[fieldKey(f)];
    if (v) return v;
    if (external && f.source === "emp:emp_code") return "(自動採番)";
    return "(未登録)";
  };

  /** 承認者の指定が途中(種類だけ)なら止める。止めたら false */
  const approverReady = (): boolean => {
    if (approverKind && approverId == null) {
      // 種類だけ選んで対象が空のまま出すと approver が黙って外れ、誰でも承認できる申請になる(Codex 中 5)
      void message.error("承認者を選ぶ(種類だけでは指定にならない。指定しないなら種類を「指定しない」に戻す)");
      return false;
    }
    return true;
  };

  /** 入力中の従業員と一緒に出す入社申請の本文(POST /api/hr/onboarding の request) */
  const requestBody = (): OnboardingRequestInput => ({
    template_id: templateId ?? null,
    items: itemIds,
    steps: stepIds,
    fields,
    start_date: startDate,
    ...(approverKind && approverId != null ? { approver: { kind: approverKind, id: approverId } } : {}),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createRequest({
        kind,
        employee_id: empId!,
        template_id: templateId ?? null,
        items: itemIds,
        remove_items: kind === "update" ? removeItemIds : [],
        steps: stepIds,
        fields,
        return_loan_ids: kind === "update" ? returnLoanIds : [],
        start_date: startDate,
        changes:
          orgUnitId || positionId != null || employmentType
            ? {
                ...(orgUnitId ? { org_unit_id: orgUnitId } : {}),
                ...(positionId != null ? { position_id: positionId } : {}),
                ...(employmentType ? { employment_type: employmentType } : {}),
              }
            : undefined,
        ...(approverKind && approverId != null ? { approver: { kind: approverKind, id: approverId } } : {}),
      }),
    onSuccess: (req) => {
      void message.success(`${kindLabel(kind)}申請 #${req.id} を出した`);
      invalidate();
      onCreated(req.id);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  /** 出す前の最後の確認(旧 Modal の onOk と同じ) */
  const submit = () => {
    if (!empId) {
      void message.error("対象者を選ぶ");
      return;
    }
    if (!approverReady()) return;
    create.mutate();
  };

  return {
    kind,
    /** 対象者が入力中の人(入社申請。まだ従業員として登録されていない) */
    isNewEmployee: !!external,
    templates,
    defaults,
    tpl,
    units,
    positions,
    employees,
    candidates,
    selected,
    term,
    setSearch,
    openLoans,
    activeItemIds,
    empId,
    setEmpId,
    templateId,
    setTemplateId,
    startDate,
    setStartDate,
    itemIds,
    setItemIds,
    removeItemIds,
    setRemoveItemIds,
    stepIds,
    setStepIds,
    fields,
    setFields,
    sourceNote,
    fixedText,
    orgUnitId,
    setOrgUnitId,
    positionId,
    setPositionId,
    employmentType,
    setEmploymentType,
    returnLoanIds,
    setReturnLoanIds,
    pickerCandidates,
    pickerLoading: picker.isFetching,
    setPickerSearch,
    approverKind,
    setApproverKind,
    approverId,
    setApproverId,
    approverPreview,
    groupsQ,
    groups: groups(tpl.data),
    dateLabel: DATE_LABEL[kind] ?? "日付",
    needsChanges: kind === "transfer" || kind === "update",
    create,
    submit,
    approverReady,
    requestBody,
  };
}

/** 対象者の行(申請の概要・確認の段で共用)。入社申請は新規登録する人を氏名・メール・契約形態・所属で見せる */
export function targetItems(draft: RequestDraft): KvItem[] {
  const { selected } = draft;
  if (draft.isNewEmployee) {
    return [
      {
        label: "対象者",
        value: selected?.name ? `${selected.name}(新規登録・入社前${selected.emp_code ? ` / ${selected.emp_code}` : ""})` : undefined,
        always: true,
      },
      { label: "メール", value: selected?.email || undefined, always: true },
      { label: "契約形態", value: selected?.employment_type || undefined, always: true },
      { label: "所属", value: selected?.department || undefined },
    ];
  }
  return [
    {
      label: "対象者",
      value: selected ? `${selected.name}(${selected.emp_code} / ${statusLabel(selected.status)})` : undefined,
      always: true,
    },
  ];
}
